/**
 * Read-only queries over a `GameState` shared by commands, views and the higher layers.
 */
import { invariant } from '../../kernel/errors';
import type { BattlefieldState, GameState, PlayerState } from '../state/game-state';
import type { BattlefieldId, ObjectId, PlayerId } from '../state/ids';
import type { GameObject } from '../state/objects';

/** The object with `id`, or `undefined`. */
export function findObject(state: GameState, id: string): GameObject | undefined {
  return (state.objects as Record<string, GameObject | undefined>)[id];
}

/** The object with `id`; throws `EngineError('invariant')` when it doesn't exist. */
export function getObject(state: GameState, id: ObjectId): GameObject {
  const object = findObject(state, id);
  invariant(object, `Unknown object ${id}`);
  return object;
}

/** The player state for an id, or `undefined`. */
export function findPlayer(state: GameState, id: string): PlayerState | undefined {
  return (state.players as Record<string, PlayerState | undefined>)[id];
}

/** A battlefield location by id, or `undefined`. */
export function findBattlefield(state: GameState, id: string): BattlefieldState | undefined {
  return state.battlefields.find((b) => b.id === id);
}

/** A battlefield location by id; throws `EngineError('invariant')` when missing. */
export function getBattlefield(state: GameState, id: BattlefieldId): BattlefieldState {
  const bf = findBattlefield(state, id);
  invariant(bf, `Unknown battlefield ${id}`);
  return bf;
}

/** Seats in seating order. */
export function seatOrder(state: GameState): PlayerId[] {
  return Object.values(state.players)
    .sort((a, b) => a.seat - b.seat)
    .map((p) => p.id);
}

/** Players still in the game (not conceded or eliminated), in seating order. */
export function playersInGame(state: GameState): PlayerId[] {
  return seatOrder(state).filter((id) => state.players[id]?.status === 'playing');
}

/** Whether the player is still in the game. */
export function isInGame(state: GameState, id: PlayerId): boolean {
  return findPlayer(state, id)?.status === 'playing';
}

/** Every other player still in the game. */
export function opponentsOf(state: GameState, id: PlayerId): PlayerId[] {
  return playersInGame(state).filter((p) => p !== id);
}

/** The next player still in the game after `id` in turn order (CR 115.1.c), or `id` when alone. */
export function nextInTurnOrder(state: GameState, id: PlayerId): PlayerId {
  const order = state.turn.order;
  const start = order.indexOf(id);
  for (let step = 1; step <= order.length; step++) {
    const candidate = order[(start + step) % order.length];
    if (candidate !== undefined && isInGame(state, candidate)) return candidate;
  }
  return id;
}

/** Objects attached to `id` (CR 719: the top-most card and its attachments). */
export function attachmentsOf(state: GameState, id: ObjectId): GameObject[] {
  return Object.values(state.objects).filter((o) => o.attachedTo === id);
}

/** Whether the game has started (setup finished) and not ended. */
export function isPlaying(state: GameState): boolean {
  return state.setup === null && state.result === null;
}
